import { useMemo } from 'react';

import type { RegionalConfigurationQueryVariables } from '@/graphql/__generated/gql/graphql';
import useRegion from '@/hooks/providers/useRegion';
import { MfeNames } from '@/utils/domains';
import { useRequest } from '@/utils/request';

import { cacheKey, query } from '../constants';
import { getUseRegionalConfigurationMethods } from '../utils/getUseRegionalConfigurationMethods';
import { normalizeCacheKeyAppName } from '../utils/normalizeCacheKeyAppName';

export type UseRegionalConfiguration = (params?: {
    appName?: MfeNames;
    region?: string;
}) => ReturnType<typeof getUseRegionalConfigurationMethods> & {
    isLoading: boolean;
    isError: boolean;
};

export const useRegionalConfiguration: UseRegionalConfiguration = (params) => {
    const { region: defaultRegion } = useRegion();
    const { appName, region = defaultRegion } = params ?? {};
    const cacheKeyApp = normalizeCacheKeyAppName(appName);
    const concatedCacheKey = `${cacheKey}-${cacheKeyApp}`;
    // Data Fetch
    const { data, isLoading, isError } = useRequest({
        cacheKey: concatedCacheKey,
        query,
        appName,
        dataParams: { region: region?.toLowerCase() } as RegionalConfigurationQueryVariables,
    });

    // Setup
    const { getConfigByKey, getConfig } = useMemo(
        () =>
            getUseRegionalConfigurationMethods({
                data: data?.regionalConfiguration,
            }),
        [data?.regionalConfiguration],
    );

    // Delivery
    return {
        getConfigByKey,
        getConfig,
        isLoading,
        isError,
    };
};
